import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Modal from './Modal';
import SelectFileDropZone from './SelectFileDropZone';
import ConversionProcess from './ConversionProcess';
import ResolveFonts from './ResolveFonts';
import ResolveSizeFormat from './ResolveSizeFormat';
import SetTemplateName from './SetTemplateName';
import UploadMissingFontError from './UploadMissingFontError';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import { useMediaQuery } from '@chakra-ui/react';

import {
  useUploadTemplateMutation,
  useSendUploadInformationMutation,
} from 'modules/app/appApi';

import { useLazyGetUserFontsQuery } from 'modules/templates/templatesApi';

export default function DesignImporterModal({
  open,
  onOpen,
  onClose,
  refresh,
}) {
  const dispatch = useDispatch();

  const [modalState, setModalState] = useState({
    title: '',
    description: '',
  });

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [templateInformation, setTemplateInformation] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [fileFormat, setFileFormat] = useState(null);
  const [fontsImported, setFontsImported] = useState([]);
  const [missingFonts, setMissingFonts] = useState([]);
  const [fontsResolved, setFontsResolved] = useState({});
  const [designId, setDesignId] = useState(null);
  const [isLargerThan750] = useMediaQuery('(min-width: 750px)');
  const [currentFonts, setCurrentFonts] = useState([]);
  const [templateHeight, setTemplateHeight] = useState(0);
  const [uploadFontError, setUploadFontError] = useState({
    isOpen: false,
    uploaded: '',
    target: '',
  });

  // Selectors
  const sessionUser = useSelector(state => state.app.user);

  const [uploadTemplate] = useUploadTemplateMutation();
  const [getUserFonts] = useLazyGetUserFontsQuery();
  const [sendUploadInformation] = useSendUploadInformationMutation();

  const onSuccess = () =>
    dispatch(
      pushNotificationWithTimeout({
        type: 'success',
        message: 'Template import successfully',
      })
    );

  const clearState = () => {
    setFile(null);
    setStep(0);
    setTemplateInformation(null);
    setTemplateFile(null);
    setFileFormat(null);
    setMissingFonts([]);
    setFontsImported([]);
    setFontsResolved({});
    setDesignId(null);
  };

  const onModalClose = () => {
    onClose();
    clearState();
  };

  const updateMissingFonts = fontMissing => {
    setMissingFonts(fontMissing);
    const missingObj = {};
    fontMissing.map(fm => {
      missingObj[fm] = false;
      return fm;
    });
    setFontsResolved(missingObj);
  };

  const addNewFont = fontMissing => {
    setFontsImported(fontMissing);
  };

  const getFileFormat = templateInformation => {
    const fileFormat = templateInformation.input.key.includes('psd')
      ? 'psd'
      : 'ai';
    setFileFormat(fileFormat);
  };

  const uploadFile = async () => {
    const formData = new FormData();
    formData.append('files[0]', file);

    try {
      const uploadedTemplate = await uploadTemplate(formData).unwrap();

      const design_file = {
        company_id: sessionUser?.company_id,
        design_file: {
          bucket: uploadedTemplate.items[0].storage.bucket,
          key: uploadedTemplate.items[0].storage.path,
        },
      };

      const templateInformation = await sendUploadInformation(
        design_file
      ).unwrap();

      setTemplateInformation(templateInformation);
      getFileFormat(templateInformation);

      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Upload template file successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Failed to uplaod a template file',
        })
      );
    }
  };

  const getMissingFonts = async templateFile => {
    try {
      const userFontsData = await getUserFonts().unwrap();
      const userFonts = userFontsData.data.data;
      setCurrentFonts(userFonts);

      let textLayers = [];
      let fonts = textLayers;
      const concatFonts = Object.values(userFonts.public).concat([
        userFonts.private,
      ]);

      templateFile.ad_template_vars.map(layer => {
        if (layer.res_type === 'txt_template') {
          textLayers.push(layer.metadata.postscriptname);
        }
        return layer;
      });

      textLayers = textLayers.filter((element, index) => {
        return textLayers.indexOf(element) === index;
      });

      //get missing fonts base on account fonts
      textLayers.map(textElement => {
        concatFonts.map(filterFonts => {
          filterFonts.map(filterFont => {
            if (filterFont.postscriptname === textElement) {
              fonts = fonts.filter(element => element !== textElement);
            }
            return filterFont;
          });
          return filterFonts;
        });
        return textElement;
      });

      fonts = fonts.filter((element, index) => {
        return fonts.indexOf(element) === index;
      });

      updateMissingFonts(fonts);

      return fonts;
    } catch (error) {
      console.log('Failed to get user fonts: ', error);
      updateMissingFonts([]); // Optionally clear if request fails
      return [];
    }
  };

  // React.useEffect(() => {
  //   const onEscapePressed = ev => ev.key === 'Escape' && onModalClose();
  //   document.addEventListener('keydown', onEscapePressed);
  //   return () => document.removeEventListener('keydown', onEscapePressed);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <>
      <Modal
        open={open}
        onClose={onModalClose}
        step={step}
        modalState={modalState}
        isLargerThan750={isLargerThan750}
      >
        <>
          {!step && (
            <SelectFileDropZone
              file={file}
              setFile={setFile}
              sm={isLargerThan750}
              onImportClicked={() => {
                setStep(1);
                setModalState({ title: 'Uploading', description: '' });
              }}
              setModalState={setModalState}
            />
          )}

          {step === 1 && (
            <ConversionProcess
              uploadFile={uploadFile}
              templateInformation={templateInformation}
              saveTemplateFile={template => {
                setTemplateFile(template);
                setTemplateHeight(template.size_format.pixel_height);
              }}
              designId={designId}
              setDesignId={setDesignId}
              setStep={setStep}
              updateMissingFonts={updateMissingFonts}
              fileFormat={fileFormat}
              setModalState={setModalState}
              sm={isLargerThan750}
              getMissingFonts={getMissingFonts}
              currentFonts={currentFonts}
            />
          )}

          {step === 2 && (
            <ResolveFonts
              addNewFont={addNewFont}
              setStep={setStep}
              fileFormat={fileFormat}
              fontsImported={fontsImported}
              missingFonts={missingFonts}
              setTemplateFile={setTemplateFile}
              // template={template}
              templateFile={templateFile}
              updateMissingFonts={updateMissingFonts}
              setModalState={setModalState}
              sm={isLargerThan750}
              fontsResolved={fontsResolved}
              setFontsResolved={setFontsResolved}
              currentFonts={currentFonts}
              setCurrentFonts={setCurrentFonts}
              getMissingFonts={getMissingFonts}
              setUploadFontError={setUploadFontError}
            />
          )}

          {step === 3 && (
            <ResolveSizeFormat
              setModalState={setModalState}
              templateFile={templateFile}
              setTemplateFie={setTemplateFile}
              setStep={setStep}
              sm={isLargerThan750}
              setTemplateFile={setTemplateFile}
              onModalClose={onModalClose}
            />
          )}

          {step === 4 && (
            <SetTemplateName
              setModalState={setModalState}
              setStep={setStep}
              sm={isLargerThan750}
              closeModal={onModalClose}
              templateFile={templateFile}
              currentFonts={currentFonts}
              refresh={refresh}
              file={file}
              onSuccess={onSuccess}
              templateHeight={templateHeight}
            />
          )}
        </>
      </Modal>
      <UploadMissingFontError
        uploadedFont={uploadFontError.uploaded}
        targetFont={uploadFontError.target}
        isOpen={uploadFontError.isOpen}
        onClose={() =>
          setUploadFontError({ isOpen: false, uploaded: '', target: '' })
        }
      />
    </>
  );
}
